<template>
  <div class="panel">
    <div class="panel-content">
      <div class="panel-title space">
        <q-btn flat dense
          icon="keyboard_arrow_left"
          size="md"
          @click="$router.go(-1)"
        />
        {{ t('title') }}
      </div>
      <div class="q-py-md" style="max-width: 100%">
        <!-- Main -->
        <div class="q-gutter-md q-py-sm">
          <q-select filled v-model="layerParams.format" :options="layerFormatOptions" :label="t('layerFormat')" />
        </div>
        <div class="q-gutter-md q-py-sm">
          <q-input clearable square outlined v-model="layerParams.name" :label="t('layerName')" />
        </div>
        <div class="q-gutter-md q-py-sm">
          <url-file-input
            v-if="layerParams.format === 'GeoJSON'"
            @input="urlFileInput"
            :label="t('layerURL')"
            accept="application/geo+json,.json,.geojson"
          />
          <url-file-input
            v-if="layerParams.format === 'GML'"
            @input="urlFileInput"
            :label="t('layerURL')"
            accept="application/gml,.gml"
          />
          <q-input v-else clearable square outlined v-model="layerParams.url" :label="t('layerURL')" />
        </div>
        <div v-show="layerParams.format" class="q-py-md">
          <strong>{{ t('properties') + ' ' + layerParams.format + ':' }}</strong>
          <!-- WMS -->
          <div class="q-gutter-md q-py-sm" v-show="layerParams.format == 'WMS'">
            <q-input square outlined v-model="layerParams.wmsLayers" :label="t('layerWMSLayers')" :hint="t('layerWMSLayersHint')" />
          </div>
          <div class="q-gutter-md" v-show="layerParams.format == 'WMS'">
            <q-toggle
              v-model="layerParams.wmsSingleImage"
              :label="t('layerWMSSingleImage')"
              color="primary"
              keep-color
            />
          </div>
          <!-- TMS -->
          <div class="q-gutter-md" v-show="layerParams.format == 'TMS'">
            <q-toggle
              v-model="layerParams.tmsInvert"
              :label="t('layerTMSInvert')"
              color="primary"
              keep-color
            />
          </div>
          <!-- WMS & TMS -->
          <div class="q-gutter-md q-py-sm" style="max-width: 150px" v-show="layerParams.format == 'WMS' || layerParams.format == 'TMS'">
            <q-input square outlined v-model="layerParams.projection" :label="t('layerProjection')" />
          </div>
          <!-- GeoJSON -->
          <div class="q-gutter-md q-py-sm row items-start" v-show="layerParams.format == 'GeoJSON' || layerParams.format == 'GML'">
            <q-input
              filled
              :label="t('geojsonColor')"
              v-model="layerParams.style.color"
              class="my-input"
            >
              <template v-slot:append>
                <q-icon name="colorize" class="cursor-pointer">
                  <q-popup-proxy transition-show="scale" transition-hide="scale">
                    <q-color v-model="layerParams.style.color" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
            <q-input v-model="layerParams.style.weight" filled :label="t('geojsonWeight')" :hint="t('geojsonWeightHint')" />
          </div>
          <div class="q-gutter-md q-py-sm row items-start" v-show="layerParams.format == 'GeoJSON' || layerParams.format == 'GML'">
            <q-input
              filled
              :label="t('geojsonFillColor')"
              v-model="layerParams.style.fillColor"
              class="my-input"
            >
              <template v-slot:append>
                <q-icon name="colorize" class="cursor-pointer">
                  <q-popup-proxy transition-show="scale" transition-hide="scale">
                    <q-color v-model="layerParams.style.fillColor" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="q-py-md" v-if="layerParams.format !== 'GML'">
            <strong>{{ 'Forçar refresc de la capa:' }}</strong>
            <div class="q-gutter-md q-py-sm">
              <q-input type="number" square outlined v-model="layerParams.minutesNextRefresh" label="Cada quants minuts vols actualitzar la capa" hint="S'actulaitzaran les dades de la capa segons els minuts indicats, si és 0, no actualitzarà les dades" />
            </div>
          </div>
        </div>
        <!-- Acceptar -->
        <div class="row q-py-md justify-end">
          <br>
          <q-btn color="primary" icon-right="layers" :disable="!enableAddLayer" :label="t('addButton')" @click="addNewLayer()" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QSelect, QInput, QToggle, QIcon, QColor, QPopupProxy, QBtn } from 'quasar'
import _ from 'lodash'

import UrlFileInput from './UrlFileInput.vue'
import { createLayer } from 'src/lib/leafletUtils.js'

export default {
  components: {
    QSelect, QInput, QToggle, QIcon, QColor, QPopupProxy, QBtn, UrlFileInput
  },
  data () {
    return {
      layerFormatOptions: this.t('layerFormatOptions'),
      layerParams: {
        format: null,
        name: null,
        url: null,
        projection: '3857',
        wmsLayers: null,
        wmsSingleImage: false,
        tmsInvert: false,
        style: {
          color: '#E74C3C',
          weight: 3,
          fillColor: '#E74C3C'
        },
        minutesNextRefresh: 0
      },
      layersToRefresh: []
    }
  },
  computed: {
    enableAddLayer () {
      if (!this.layerParams.name || !this.layerParams.url) {
        return false
      }
      if (this.layerParams.format === 'WMS') {
        return (this.layerParams.wmsLayers && this.layerParams.projection)
      } else if (this.layerParams.format === 'TMS') {
        return !!this.layerParams.projection
      }
      return true
    },
    overlayingLayers () {
      return this.$store.state.map.layers.overlays.map(overlay => overlay.layer)
    },
    paramsFormat () {
      return this.layerParams.format
    }
  },
  methods: {
    t (key, ...args) {
      return this.$t('tools.addCustomLayer.' + key, ...args)
    },
    async addNewLayer () {
      const layerParams = _.cloneDeep(this.layerParams)
      const layer = await createLayer(layerParams)
      this.addLayerToMap(layer, layerParams)
      if (layerParams.minutesNextRefresh > 0) {
        this.$store.dispatch('customLayer/refreshLayer', { layer, layerParams })
      }
    },
    addLayerToMap (layer, layerParams) {
      this.$store.dispatch('map/addOverlay', {
        layer,
        layerType: layerParams.format,
        name: layerParams.name
      })
      if (layerParams.format === 'GML') {
        const bounds = layer.getBounds()
        this.$store.state.map.mapObject.fitBounds(bounds)
      }
    },
    urlFileInput (input) {
      this.layerParams.url = input
    }
  },
  watch: {
    paramsFormat: function () {
      this.layerParams.url = null
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
